 
/* eslint-disable @typescript-eslint/no-floating-promises */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./components/css/styles.css";
import "./index.css";
import { msalInstance, forgotPasswordRequest } from "./utils/authConfig";
import {
    AuthenticationResult,
    EventMessage,
    EventType,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";



const queryClient = new QueryClient();

// Assume Sentry is in prod, and only set non-prod mode for known environments.
const sentryIsNonProd = ["local", "development", "pre-production"].includes(
  import.meta.env.MAINSITE_SENTRY_ENVIRONMENT,
);
Sentry.init({
  dsn: "https://52dd15d3951eb8f2b4aac298f23ff289@o4505906803900416.ingest.sentry.io/4506315152162816",
  integrations: [
    new Sentry.BrowserTracing(),
    new Sentry.Replay({ useCompression: false }), // Unfortunatley compression needs to be disabled to avoid CSP errors https://github.com/getsentry/sentry-javascript/issues/6739#issuecomment-1497359414
  ],
  environment: import.meta.env.MAINSITE_SENTRY_ENVIRONMENT,
  // Performance Monitoring
  tracesSampleRate: sentryIsNonProd ? 1.0 : 0.1,
  // Session Replay
  replaysSessionSampleRate: sentryIsNonProd ? 1.0 : 0.1,
  replaysOnErrorSampleRate: sentryIsNonProd ? 1.0 : 0.1,
});

// check this link to see more information about adjusting this configuration:
// https://codesandbox.io/p/sandbox/ms-identity-react-c3s2-umpv48?file=%2Fsrc%2FApp.jsx%3A73%2C12-83%2C14


msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
  }
  msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as AuthenticationResult;
          const account = payload.account;
          msalInstance.setActiveAccount(account);
      }
      if (event.eventType === EventType.LOGIN_FAILURE) {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (event.error?.message.includes('AADB2C90118')) {
            msalInstance.loginRedirect(forgotPasswordRequest);
        }
    }
  });

  createRoot(document.getElementById("root") as HTMLElement).render(
    (
      <MsalProvider instance={msalInstance}>
        <StrictMode>
          <QueryClientProvider client={queryClient}>
              <App />
          </QueryClientProvider>
        </StrictMode>
      </MsalProvider>
    )
);
});